import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../state/store";
import {
  pushReleaseNotes,
  setCertificateUrl,
  setDevicesRefresh,
  setFleetManagementActiveTab,
  setFwuCampaingsRefresh,
  setFWUVersionsRefresh,
  setNotificationCampaignsRefresh,
  setNotificationsRefresh
} from "../../state/ducks";
import { selectReleaseNotes } from "../../state/selectors";

import { alignItems, fleetManagementSections, routePaths, strCode } from "../../consts";
import { navigationService } from "../../services";
import { fleetManagementUtil, fwuCampaignUtils } from "../../utils";

import {
  useCreateCertificates,
  useDownloadCertificates,
  useFetchCertificateInfo,
  useCreateDeviceChangeHistoryRecord,
  useFetchDeviceChangeHistory,
  useCreateDevice,
  useCreateDeviceBatch,
  useDeleteDevice,
  useResetDevice,
  useCreateFWUCampaign,
  useDeactivateFWUCampaign,
  useFetchFWUCampaignDetails,
  useUpdateFWUCampaign,
  useDeleteFWUVersion,
  useFetchReleaseNotes,
  useUpdateFWUVersion
} from "../../apiHooks";
import { useFetchDeviceData } from "./hooks/useFetchDeviceData";
import { useFetchFWUCampaignData } from "./hooks/useFetchFWUCampaignData";

import { FlexContainers, PageContainer, PageSelector, WrapperContainer } from "../../components";
import {
  DeviceBatchCreateModal,
  DeviceTable,
  FleetManagementControls,
  FWUCampaignReportModal,
  FWUCampaignTable,
  NewDeviceModal,
  NewFWUCampaignModal,
  FirmwaresTable,
  FirmwareDeleteModal,
  CampaignsCounter,
  CertificatesModal,
  DeviceBatchCertificatesDownload
} from "../../containers";
import { PageSelectorWrapper } from "./styled";

const fleetManagementPages = [
  { id: fleetManagementSections.FWU_CAMPAIGNS, title: strCode.FIRMWARE_CAMPAIGNS_TITLE },
  { id: fleetManagementSections.FIRMWARES, title: strCode.FIRMWARES_TITLE },
  {
    id: fleetManagementSections.DEVICES,
    title: strCode.DEVICES_TITLE
  }
];

const MAX_ACTIVE_CAMPAIGNS_COUNT = 100;

const FleetManagementPage = () => {
  const [deviceSearchValue, setDeviceSearchValue] = useState("");

  const [firmwareSearchValue, setFirmwareSearchValue] = useState("");
  const [fwuCampaignSearchValue, setFwuCampaignSearchValue] = useState("");
  const [firmwareDeleteModalActive, setFirmwareDeleteModalActive] = useState(false);

  const [newDeviceModalActive, setNewDeviceModalActive] = useState(false);
  const [batchDeviceModalActive, setBatchDeviceModalActive] = useState(false);
  const [deviceCertificates, setDeviceCertificates] = useState([]);
  const [downloadCertificatesActive, setDownloadCertificatesActive] = useState(false);

  const [newFWUCampaignModalActive, setNewFWUCampaignModalActive] = useState(false);
  const [isReportModalActive, setIsReportModalActive] = useState(false);

  const [isCertificatesModalActive, setIsCertificatesModalActive] = useState(false);

  const deviceToEdit = useSelector((store) => store.fleetManagement?.deviceToEdit);
  const fwuCampaignToEdit = useSelector((store) => store.fwuCampaigns?.fwuCampaignToEdit);

  const fwuVersionToDelete = useSelector((store) => store.fwuVersions?.fwuVersionToDelete);

  const marketFilter = useSelector((store) => store.configs?.userMarketFilter);
  const marketIso = useSelector((store) => store.configs?.marketIso);

  const fleetManagementActiveTab = useSelector((store) => store.global?.fleetManagementActiveTab);

  const [activePage, setActivePage] = useState(
    fleetManagementActiveTab || fleetManagementSections.FWU_CAMPAIGNS
  );

  const preparedMarket = { value: marketFilter ?? marketIso, id: marketFilter ?? marketIso };
  const [marketOption, setMarketOption] = useState(preparedMarket);

  const selectedFWUCampaignName = useMemo(() => {
    return fwuCampaignToEdit?.campaignName;
  }, [fwuCampaignToEdit]);

  const {
    devices,
    mercuryDevices,
    deviceGroups,
    devicesLoading,
    deviceGroupsLoading,
    deviceGroupChangesLoading,
    deviceGroupListChangesLoading,
    createDeviceGroup,
    updateDeviceGroup,
    deleteDeviceGroup,
    updateDeviceGroupsList,
    deleteDeviceGroupsList
  } = useFetchDeviceData(activePage);

  const {
    mutationCallback: createDevice,
    loading: createDeviceLoading,
    error: createDeviceError
  } = useCreateDevice();

  const {
    mutationCallback: createDeviceBatch,
    loading: createDeviceBatchLoading,
    error: batchCreateError
  } = useCreateDeviceBatch();

  const {
    mutationCallback: deleteDevice,
    loading: deleteDeviceLoading,
    error: deleteDeviceError
  } = useDeleteDevice();

  const { mutationCallback: resetDevice, loading: resetDeviceLoading } = useResetDevice();

  const {
    fetchCallback: fetchCertificateInfo,
    data: certificatesInfo,
    loading: certificateInfoLoading
  } = useFetchCertificateInfo();

  const { fetchCallback: downloadCertificates, loading: downloadCertificatesLoading } =
    useDownloadCertificates();

  const {
    mutationCallback: createCertificates,
    data: certificateUrl,
    loading: createCertificatesLoading,
    error: createCertificatesError
  } = useCreateCertificates();

  const {
    fetchCallback: fetchDeviceChangeHistory,
    data: deviceChangeHistory,
    loading: deviceChangeHistoryLoading,
    refresh: deviceChangeHistoryRefresh
  } = useFetchDeviceChangeHistory();

  const { loading: createDeviceChangeHistoryLoading } = useCreateDeviceChangeHistoryRecord();

  const { fetchCallback: fetchReleaseNotes, loading: releaseNotesLoading } = useFetchReleaseNotes();

  useEffect(() => {
    if (deviceToEdit) {
      setNewDeviceModalActive(true);
    }
  }, [deviceToEdit]);

  useEffect(() => {
    setFirmwareDeleteModalActive(!!fwuVersionToDelete);
  }, [fwuVersionToDelete]);

  const {
    fwuCampaigns,
    firmwares,
    activeFWUCampaignsCount,
    hasMaxActiveFwuCampaigns,
    fwuCampaignsLoading,
    notificationsLoading,
    notificationCampaignsLoading,
    fwuVersionsLoading
  } = useFetchFWUCampaignData(marketOption);

  const {
    fetchCallback: fetchFWUCampaignDetails,
    data: fwuCampaignDetails,
    loading: fwuCampaignDetailsLoading,
    refresh: fwuCampaignDetailsRefresh
  } = useFetchFWUCampaignDetails();

  const {
    mutationCallback: createFWUCampaign,
    loading: createFWUCampaignLoading,
    error: createFWUCampaignError
  } = useCreateFWUCampaign();

  const {
    mutationCallback: updateFWUCampaign,
    loading: updateFWUCampaignLoading,
    error: updateFWUCampaignError
  } = useUpdateFWUCampaign();

  const {
    mutationCallback: deactivateFWUCampaign,
    loading: deactivateFWUCampaignLoading,
    error: deactivateFWUCampaignError
  } = useDeactivateFWUCampaign();

  const { mutationCallback: updateFWUVersion } = useUpdateFWUVersion();
  const { mutationCallback: deleteFWUVersion, loading: deleteFWUVersionLoading } =
    useDeleteFWUVersion();

  useEffect(() => {
    if (downloadCertificatesActive === false) {
      dispatch(setCertificateUrl(null));
      setDeviceCertificates([]);
    }
  }, [downloadCertificatesActive]);

  useEffect(() => {
    if (fwuCampaignToEdit && !newFWUCampaignModalActive && !isReportModalActive) {
      const isScheduled = fwuCampaignUtils.getIsScheduled(fwuCampaignToEdit);

      if (isScheduled) {
        setNewFWUCampaignModalActive(true);
      } else {
        setIsReportModalActive(true);
      }
    }
  }, [fwuCampaignToEdit]);

  useEffect(() => {
    if (selectedFWUCampaignName) {
      fetchFWUCampaignDetails(selectedFWUCampaignName, fwuCampaignDetailsRefresh);
    }
  }, [selectedFWUCampaignName, fwuCampaignDetailsRefresh]);

  const onCreateFwuCampaign = () => {
    setNewFWUCampaignModalActive(true);
  };

  const onRefreshAll = () => {
    if (activePage === fleetManagementSections.DEVICES) {
      dispatch(setDevicesRefresh(true));
      setDeviceSearchValue("");
    } else if (activePage === fleetManagementSections.FWU_CAMPAIGNS) {
      dispatch(setFwuCampaingsRefresh(true));
      dispatch(setFWUVersionsRefresh(true));
      dispatch(setNotificationsRefresh(true));
      setFwuCampaignSearchValue("");
    } else if (activePage === fleetManagementSections.FIRMWARES) {
      dispatch(setFwuCampaingsRefresh(true));
      dispatch(setNotificationCampaignsRefresh(true));
      dispatch(setFWUVersionsRefresh(true));
      dispatch(setNotificationsRefresh(true));
      setFirmwareSearchValue("");
    }
  };

  const onCreateDevice = () => {
    setNewDeviceModalActive(true);
  };

  const onCreateBatch = () => {
    setBatchDeviceModalActive(true);
  };

  const handleBatchCreateDevices = async (file, data, generateCertificates = false) => {
    await createDeviceBatch(file, data);

    const formattedData = fleetManagementUtil.formatDevices(data);
    const dusns = formattedData.filter((x) => x.isMercuryDevice).map((x) => x.dusn);
    if (!generateCertificates || batchCreateError || dusns.length === 0) {
      setBatchDeviceModalActive(false);
      return;
    }

    await createCertificates(dusns);

    const certificates = await fetchCertificateInfo();
    setDeviceCertificates(
      formattedData.map((device) => {
        return {
          ...device,
          ...certificates.find((certificate) => certificate.dusn === device.dusn)
        };
      })
    );

    setBatchDeviceModalActive(false);
    setDownloadCertificatesActive(true);
  };

  const handleCertificatesDownload = useCallback(async () => {
    const dusns = deviceCertificates.filter((x) => x.isMercuryDevice).map((x) => x.dusn);

    await downloadCertificates(dusns, certificateUrl);
    setDownloadCertificatesActive(false);
  }, [deviceCertificates, certificateUrl]);

  const onSetActivePage = (page) => {
    setActivePage(page);
    dispatch(setFleetManagementActiveTab(page));
  };

  const onFetchReleaseNotes = (version) => {
    const releaseNotes = selectReleaseNotes(version)(getState());

    const onSaveReleaseNotes = (releaseNotes) => {
      dispatch(pushReleaseNotes({ version, releaseNotes: releaseNotes ?? null }));
    };

    if (releaseNotes === undefined) {
      fetchReleaseNotes(version, onSaveReleaseNotes);
    }
  };

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        <FleetManagementControls
          activePage={activePage}
          devicesLoading={devicesLoading || createDeviceBatchLoading}
          fwuCampaignsLoading={fwuCampaignsLoading}
          notificationsLoading={notificationsLoading}
          fwuVersionsLoading={fwuVersionsLoading}
          onRefresh={onRefreshAll}
        />

        <PageSelectorWrapper Container={FlexContainers.FlexRowBetween}>
          <WrapperContainer>
            <PageSelector
              pages={fleetManagementPages}
              activePage={activePage}
              setActivePage={onSetActivePage}
              component={(page) => {
                return (
                  page.id === fleetManagementSections.FWU_CAMPAIGNS && (
                    <CampaignsCounter
                      activeCampaigns={activeFWUCampaignsCount}
                      maxActiveCampaigns={MAX_ACTIVE_CAMPAIGNS_COUNT}
                      loading={fwuCampaignsLoading}
                    />
                  )
                );
              }}
            />
          </WrapperContainer>
        </PageSelectorWrapper>
      </WrapperContainer>

      <WrapperContainer width={"100%"} mt={12}>
        {activePage === fleetManagementSections.FWU_CAMPAIGNS && (
          <>
            <FWUCampaignTable
              fwuCampaignData={fwuCampaigns ?? []}
              loading={fwuCampaignsLoading || notificationCampaignsLoading || fwuVersionsLoading}
              searchValue={fwuCampaignSearchValue}
              hasMaxActiveFwuCampaigns={hasMaxActiveFwuCampaigns}
              setSearchValue={setFwuCampaignSearchValue}
              onCreateFwuCampaign={onCreateFwuCampaign}
              setMarket={setMarketOption}
              selectedMarket={marketOption}
            />

            <NewFWUCampaignModal
              fwuCampaignData={fwuCampaignToEdit}
              active={newFWUCampaignModalActive}
              loading={
                createFWUCampaignLoading || updateFWUCampaignLoading || deactivateFWUCampaignLoading
              }
              releaseNotesLoading={releaseNotesLoading}
              error={createFWUCampaignError || updateFWUCampaignError || deactivateFWUCampaignError}
              setActive={setNewFWUCampaignModalActive}
              onSave={createFWUCampaign}
              onUpdate={updateFWUCampaign}
              onDeactivate={deactivateFWUCampaign}
              onFetchReleaseNotes={onFetchReleaseNotes}
            />

            <FWUCampaignReportModal
              active={isReportModalActive}
              fwuCampaignData={fwuCampaignToEdit}
              fwuCampaignDetails={fwuCampaignDetails}
              fwuCampaignDetailsLoading={fwuCampaignDetailsLoading}
              loading={
                fwuCampaignsLoading || updateFWUCampaignLoading || deactivateFWUCampaignLoading
              }
              error={updateFWUCampaignError}
              setActive={setIsReportModalActive}
              onSave={updateFWUCampaign}
              onDeactivate={deactivateFWUCampaign}
            />
          </>
        )}

        {activePage === fleetManagementSections.DEVICES && (
          <>
            <DeviceTable
              devicesData={devices ?? []}
              dusnFilter={deviceSearchValue}
              setSearchValue={setDeviceSearchValue}
              onCreateDevice={onCreateDevice}
              onCreateBatch={onCreateBatch}
              onOpenCertificates={() => setIsCertificatesModalActive(true)}
            />

            <NewDeviceModal
              deviceData={deviceToEdit}
              deviceGroups={deviceGroups}
              certificateUrl={certificateUrl}
              hasCertificate={!!(certificatesInfo && certificatesInfo[0])}
              deviceChangeHistory={deviceChangeHistory}
              active={newDeviceModalActive}
              createDeviceLoading={createDeviceLoading || deviceGroupListChangesLoading}
              deleteDeviceLoading={deleteDeviceLoading}
              resetDeviceLoading={resetDeviceLoading}
              certificatesLoading={
                certificateInfoLoading || createCertificatesLoading || downloadCertificatesLoading
              }
              certificateInfoLoading={certificateInfoLoading}
              deviceChangeHistoryLoading={
                deviceChangeHistoryLoading || createDeviceChangeHistoryLoading
              }
              deviceGroupsLoading={deviceGroupsLoading}
              deviceGroupChangesLoading={deviceGroupChangesLoading}
              deviceChangeHistoryRefresh={deviceChangeHistoryRefresh}
              error={createDeviceError || deleteDeviceError}
              setActive={setNewDeviceModalActive}
              onSave={createDevice}
              onDelete={deleteDevice}
              onReset={resetDevice}
              onFetchCertificateInfo={fetchCertificateInfo}
              onGenerateCertificates={createCertificates}
              onDownloadCertificates={downloadCertificates}
              onFetchDeviceChangeHistory={fetchDeviceChangeHistory}
              onCreateDeviceGroup={createDeviceGroup}
              onUpdateDeviceGroup={updateDeviceGroup}
              onDeleteDeviceGroup={deleteDeviceGroup}
              onUpdateDeviceGroupList={updateDeviceGroupsList}
              onDeleteDeviceGroupList={deleteDeviceGroupsList}
            />

            <DeviceBatchCreateModal
              active={batchDeviceModalActive}
              setActive={setBatchDeviceModalActive}
              onSubmit={handleBatchCreateDevices}
            />

            <CertificatesModal
              mercuryDevices={mercuryDevices}
              certificatesInfo={certificatesInfo}
              certificateUrl={certificateUrl}
              active={isCertificatesModalActive}
              loading={createCertificatesLoading}
              certificateInfoLoading={certificateInfoLoading}
              error={createCertificatesError}
              setActive={setIsCertificatesModalActive}
              onFetchCertificatesInfo={fetchCertificateInfo}
              onGenerateCertificates={createCertificates}
              onDownloadCertificates={downloadCertificates}
              onCertificatesRevoke={(selectedDevices) =>
                console.log("Revoking certificates for: " + selectedDevices)
              }
            />

            <DeviceBatchCertificatesDownload
              active={downloadCertificatesActive}
              devices={deviceCertificates}
              setActive={setDownloadCertificatesActive}
              onDownload={handleCertificatesDownload}
            />
          </>
        )}

        {activePage === fleetManagementSections.FIRMWARES && (
          <>
            <FirmwaresTable
              firmwaresData={firmwares}
              searchValue={firmwareSearchValue}
              onUpdateFWUVersion={updateFWUVersion}
              onCreateFwuVersion={() => navigationService.forwardTo(routePaths.FW_VERSION_CREATE)}
              setSearchValue={setFirmwareSearchValue}
            />

            <FirmwareDeleteModal
              firmware={fwuVersionToDelete}
              loading={deleteFWUVersionLoading}
              onDeleteFWUVersion={deleteFWUVersion}
              active={firmwareDeleteModalActive}
            />
          </>
        )}
      </WrapperContainer>
    </PageContainer>
  );
};

export default FleetManagementPage;
